// client/src/hooks/useClientPortalApiClient.js
// Mirrors hooks/useApiClient.js but targets the client-portal API and reads a separate
// token key — no code path here ever touches localStorage['jwtToken'].
import { useCallback, useMemo } from 'react';

const TOKEN_KEY = 'clientPortalToken';

const forceLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    window.location.href = '/client-portal/login';
};

export const useClientPortalApiClient = () => {
    const token = localStorage.getItem(TOKEN_KEY);

    const request = useCallback(async (endpoint, options = {}, responseType = null) => {
        const config = {
            ...options,
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
                ...options.headers,
            },
        };

        const response = await fetch(endpoint, config);

        if (response.status === 401) {
            forceLogout();
            throw new Error('Your session has expired. Please log in again.');
        }

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            if (errorData.mustChangePassword) {
                window.location.href = '/client-portal/change-password';
            }
            throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
        }

        if (responseType === 'blob') {
            return { data: await response.blob() };
        }
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.indexOf('application/json') !== -1) {
            return response.json();
        }
        return null;
    }, [token]);

    return useMemo(() => ({
        get: (endpoint, opts = {}) => request(endpoint, { method: 'GET' }, opts.responseType || null),
        post: (endpoint, body) => request(endpoint, { method: 'POST', body: JSON.stringify(body) }),
        patch: (endpoint, body) => request(endpoint, { method: 'PATCH', body: JSON.stringify(body) }),
        del: (endpoint) => request(endpoint, { method: 'DELETE' }),
    }), [request]);
};
